import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from '../hooks/useProfileContext';
import { bandUrlBase, memberUrlPath } from '../utils/slug';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGetTeam, apiLinkTeamMemberBand, apiMoveRiderCardKind, type TeamMemberDTO } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import { showConfirm } from '../components/ConfirmDialog';
import { buildCardDeleteCopy, confirmPersonnelDelete } from '../lib/deletePersonnel';
import type { CrewMember } from '../types';
import PageHeader from '../components/PageHeader';

const CREW_ROLE_RANK: Record<string, number> = { main: 0, session: 1 };
const CREW_ROLE_LABELS: Record<string, string> = {
  main: 'crew.roleMain',
  session: 'crew.roleSession',
};

export default function CrewListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId, profiles, getProfile } = useProfileContext();
  const { bandSlug: urlBandSlug } = useParams();
  // Resolve the band from the URL slug FIRST (the URL is the source of truth
  // for which band's data to show — the context activeProfileId may lag or
  // be stale right after a band switch). Falls back to the context profile.
  const urlProfile = useMemo(
    () => profiles.find((p) => p.slug === urlBandSlug) || null,
    [profiles, urlBandSlug],
  );
  const bandId = urlProfile?.id || activeProfileId || '';
  const { crew, deleteCrew, reorderCrew, reload: reloadCrew } = useCrew(bandId || undefined);
  // Write gate: the explicit "Move to Musicians" action must be absent for a
  // member who cannot write this band (same boundary as POST /:id/bands).
  const canWrite = useWriteGate(bandId).canWrite;
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [selectedBaseRoles, setSelectedBaseRoles] = useState<string[]>([]);
  const sortedAllCrew = useMemo(
    () =>
      [...crew].sort((a, b) => {
        const ra = CREW_ROLE_RANK[a.baseRole || 'main'] ?? 0;
        const rb = CREW_ROLE_RANK[b.baseRole || 'main'] ?? 0;
        if (ra !== rb) return ra - rb;
        const oa = a.order ?? 0;
        const ob = b.order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.name || '').localeCompare(b.name || '');
      }),
    [crew],
  );
  const sortedCrew = useMemo(
    () => (selectedBaseRoles.length === 0
      ? sortedAllCrew
      : sortedAllCrew.filter((m) => selectedBaseRoles.includes(m.baseRole || 'main'))),
    [sortedAllCrew, selectedBaseRoles],
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, idx: number) => {
      setDragIdx(idx);
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIdx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === dropIdx) return;
      const ids = sortedCrew.map((m) => m.id);
      const [moved] = ids.splice(dragIdx, 1);
      ids.splice(dropIdx, 0, moved);
      reorderCrew(ids);
      setDragIdx(null);
    },
    [dragIdx, sortedCrew, reorderCrew],
  );
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [linking, setLinking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAllBands, setShowAllBands] = useState(false);

  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandSlug = activeProfile?.slug || activeProfileId || '';
  const base = activeProfileId ? bandUrlBase(bandSlug, activeProfile?.name) : '';

  // Load candidates from the Team registry. We fetch the whole registry (not
  // band-scoped) so the "Show members from all bands" toggle can reveal
  // members of other bands for multi-band adds. The default view filters to
  // the selected band only (see availableMembers below).
  useEffect(() => {
    if (!bandId) return;
    let cancelled = false;
    apiGetTeam()
      .then((members) => { if (!cancelled) setTeamMembers(members); })
      .catch(() => { if (!cancelled) setTeamMembers([]); });
    return () => { cancelled = true; };
  }, [bandId, reloadKey]);

  // Default: only members already linked to the selected band (via
  // team_member_bands), and not yet present on this band's rider. When
  // "Show members from all bands" is ticked, reveal every registry member so
  // a person from another band can be added here (multi-band support).
  // A person may be musician in one band and crew in another (user decision
  // 2026-10-12): eligibility for THIS page is NOT the global category. A
  // candidate is a person linked to the band (or any registry member when "all
  // bands" is on) who holds NO card yet in EITHER list of this band — the rider
  // must never count somebody twice. `card_kind` is the server truth per band.
  const availableMembers = useMemo(
    () => teamMembers
      .filter((m) => showAllBands || (m.bands || []).some((b) => b.id === bandId))
      .filter((m) => !(m.bands || []).some((b) => b.id === bandId && b.card_kind))
      // Comfort ordering: people whose GLOBAL category matches this page come
      // first, then everyone else, then by name.
      .sort((a, b) => {
        const ca = a.category === 'crew' ? 0 : 1;
        const cb = b.category === 'crew' ? 0 : 1;
        if (ca !== cb) return ca - cb;
        return a.name.localeCompare(b.name);
      }),
    [teamMembers, bandId, showAllBands],
  );

  // Identity sync: the display name comes from the Team registry (nickname ||
  // name) when the rider card shares the same member id — a nickname edited on
  // the Team page shows up here too.
  const teamById = useMemo(() => {
    const map = new Map<string, TeamMemberDTO>();
    for (const m of teamMembers) map.set(m.id, m);
    return map;
  }, [teamMembers]);
  const displayName = (member: { id: string; name?: string }): string => {
    const tm = teamById.get(member.id);
    if (tm?.nickname) return tm.nickname;
    return member.name || t('crew.unnamed');
  };

  // Dropdown label for an "add existing member" candidate. Prefer the
  // descriptive role (e.g. "sound engineer", "Driver"); fall back to the
  // meaningful tags (category is implied by the page, so drop the redundant
  // primary category tag — otherwise a person with both a category tag and a
  // role-specific tag reads as "crew, musician" instead of their real role).
  const memberRoleLabel = (m: TeamMemberDTO): string => {
    if (m.role && m.role.trim()) return m.role.trim();
    const meaningful = (m.tags || []).filter((tag) => tag !== m.category);
    if (meaningful.length) return meaningful.join(', ');
    return t('crew.noRole');
  };

  // Option C: Team people linked to THIS band who don't yet have a rider card
  // are shown as additional cards, so the band's Crew page always reflects the
  const addExistingMember = async () => {
    if (!bandId || !selectedMemberId) return;
    setLinking(true);
    try {
      // Post the band-specific role: the SERVER mints the card of that kind, so a
      // musician-category person can join this band as crew.
      const res = await apiLinkTeamMemberBand(selectedMemberId, bandId, 'crew');
      reportSaveOk('crew');
      // Safety net: a same-band card of the other kind already exists ⇒ nothing
      // minted (never a duplicate). Point the user at the explicit move.
      if (res.card?.conflict) window.alert(t('crew.moveConflict'));
      setSelectedMemberId('');
      setTeamMembers((prev) => prev.filter((x) => x.id !== selectedMemberId));
      setReloadKey((k) => k + 1);
      // Refetch the band's rider crew so the freshly-minted card appears
      // immediately (the server created the rider_crew row on link; without
      // this the user had to refresh the page to see the new crew card).
      await reloadCrew();
    } catch (err) {
      reportSaveError('crew', err);
    } finally {
      setLinking(false);
    }
  };

  // Explicit move of a card from this band's Crew list to its Musicians list.
  const moveCard = async (memberId: string) => {
    if (!bandId || !canWrite) return;
    if (!(await showConfirm(t('crew.moveToMusiciansConfirm')))) return;
    try {
      await apiMoveRiderCardKind(memberId, bandId, 'musician');
      await reloadCrew();
      reportSaveOk('crew');
    } catch (err) {
      reportSaveError('crew', err);
    }
  };

  const handleAdd = () => {
    // Let CrewEditPage create the record after the user has entered its data.
    // Navigating to a slug such as "new-crew" makes the edit route treat it as
    // an existing member and leaves the form stuck in its loading state.
    navigate(`${base}/crew/new`);
  };

  // LOT A (2026-10-15): the crew card deletion used to fire straight from the
  // click with no warning. Name WHO leaves and WHICH band, and state that the
  // person survives in Team and in their other bands.
  const handleDelete = async (member: CrewMember) => {
    if (!(await confirmPersonnelDelete(buildCardDeleteCopy(member, 'crew', t)))) return;
    deleteCrew(member.id);
  };

  return (
    <div className="page">
      {/* P3a — unified page header (mono title + sub-line + action right). */}
      <PageHeader
        title={t('crew.title')}
        subtitle={t('crew.subtitle')}
        actions={
          <button className="btn btn-primary" onClick={handleAdd}>
            {t('crew.addCrew')}
          </button>
        }
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('crew.baseRole')}</span>
        <button
          type="button"
          className={`rc-pill${selectedBaseRoles.length === 0 ? ' on' : ''}`}
          data-role-filter="all"
          onClick={() => setSelectedBaseRoles([])}
        >
          {t('crew.all')}
        </button>
        {(['main', 'session'] as const).map((br) => (
          <button
            key={br}
            type="button"
            className={`rc-pill${selectedBaseRoles.includes(br) ? ' on' : ''}`}
            data-role-filter={br}
            onClick={() => setSelectedBaseRoles((prev) =>
              prev.includes(br) ? prev.filter((x) => x !== br) : [...prev, br],
            )}
          >
            {t(CREW_ROLE_LABELS[br] || br)}
          </button>
        ))}
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
          {sortedCrew.length} / {crew.length}
        </span>
      </div>

      {activeProfileId && (
        <div className="card" style={{ padding: 12, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: 2, minWidth: 220, marginBottom: 0 }}>
            <label>{t('crew.addExistingTeam')}</label>
            <select
              className="form-input"
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
            >
              <option value="">{t('crew.choosePerson')}</option>
              {availableMembers.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({memberRoleLabel(m)})</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary"
            disabled={!selectedMemberId || linking}
            onClick={addExistingMember}
          >
            {linking ? t('crew.linking') : t('crew.linkToBand')}
          </button>
          <label className="form-check" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={showAllBands}
              onChange={(e) => setShowAllBands(e.target.checked)}
            />
            {t('crew.showAllBands')}
          </label>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {availableMembers.length === 0
              ? t('crew.noTeamLeft')
              : t('crew.teamAvailable', { count: availableMembers.length })}
          </span>
        </div>
      )}

      {crew.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)', marginBottom: 16 }}>
            {t('crew.noCrew')}
          </p>
        </div>
      )}

      <div className="musician-grid">
        {sortedCrew.map((member, idx) => {
          const role = member.baseRole || 'main';
          const prev = idx > 0 ? sortedCrew[idx - 1] : null;
          const prevRole = prev ? prev.baseRole || 'main' : null;
          const isNewSection = idx === 0 || prevRole !== role;
          return (
            <Fragment key={member.id}>
              {isNewSection && (
                <div className="musician-section-label">
                  {t(CREW_ROLE_LABELS[role] || role)}
                </div>
              )}
              <div
                className={`musician-card ${dragIdx === idx ? 'dragging' : ''}`}
                data-crew-card={member.id}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={() => setDragIdx(null)}
              >
                <div
                  className="musician-color-bar"
                  style={{ backgroundColor: member.color || 'var(--info)' }}
                />
                <button
                  type="button"
                  className="musician-drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragEnd={() => setDragIdx(null)}
                  title={t('crew.dragToReorder')}
                  aria-label={t('crew.dragToReorder')}
                >
                  ⋮⋮
                </button>
                <div
                  className="musician-card-body"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`${base}/crew/${memberUrlPath(member.id, member.name || member.role)}`)}
                >
                  <h3>{displayName(member)}</h3>
                  <p className="musician-role">
                    {(member.tags && member.tags.filter((x) => x !== 'crew' && x !== 'musician').length > 0
                      ? member.tags.filter((x) => x !== 'crew' && x !== 'musician').join(', ')
                      : member.role === 'crew'
                        ? ''
                        : member.role) || t('crew.noRole')}
                  </p>
                  <div className="musician-details">
                    {member.iemType && member.iemType !== 'none' && (
                      <span>🎧 {member.iemType === 'stereo' ? t('crew.stereoIem') : t('crew.monoIem')}</span>
                    )}
                    {member.wedges > 0 && <span>{t('crew.wedges', { count: member.wedges })}</span>}
                    {member.subwoofer && <span>{t('crew.sub')}</span>}
                  </div>
                  <div className="musician-details">
                    {member.diet && member.diet !== 'none' && <span>🍽 {member.diet}</span>}
                    {member.towels > 0 && <span>{t('crew.towels', { count: member.towels })}</span>}
                  </div>
                  <p className="instrument-count">
                    {(member.backline || []).length > 0 && t('crew.backline', { count: member.backline.length })}
                  </p>
                  <div className="musician-card-actions">
                    <button
                      className="btn btn-small"
                      onClick={(e) => { e.stopPropagation(); navigate(`${base}/crew/${memberUrlPath(member.id, member.name || member.role)}`); }}
                    >
                      {t('crew.edit')}
                    </button>
                    {canWrite && (
                      <button
                        className="btn btn-small"
                        data-move-card={member.id}
                        onClick={(e) => { e.stopPropagation(); moveCard(member.id); }}
                      >
                        {t('crew.moveToMusicians')}
                      </button>
                    )}
                    <button
                      className="btn btn-small btn-danger"
                      onClick={(e) => { e.stopPropagation(); handleDelete(member); }}
                    >
                      {t('crew.delete')}
                    </button>
                  </div>
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
